import { ButtonType, InputText } from "@constellation-academy/epic-ui";
import { Dialog } from "primereact/dialog";
import { useState } from "react";
import { useFragment, useMutation } from "react-relay";
import { toast } from "react-toastify";
import { Button, ButtonVariant } from "@components/button";
import { KnownGapInfo } from "@components/known-gap-info";
import { isOrderPaid } from "@features/orders/order-paid-status";
import { useTranslation } from "@i18n/hooks";
import { type invoiceEmailActions_CorrectInvoiceEmailMutation } from "@relay/invoiceEmailActions_CorrectInvoiceEmailMutation.graphql";
import { type invoiceEmailActions_OrderFragment$key } from "@relay/invoiceEmailActions_OrderFragment.graphql";
import { type invoiceEmailActions_ResendInvoiceMutation } from "@relay/invoiceEmailActions_ResendInvoiceMutation.graphql";
import { type invoiceEmailActions_SendGuestOrderClaimEmailMutation } from "@relay/invoiceEmailActions_SendGuestOrderClaimEmailMutation.graphql";
import {
	CORRECT_INVOICE_EMAIL_MUTATION,
	ORDER_FRAGMENT,
	RESEND_INVOICE_MUTATION,
	SEND_CLAIM_EMAIL_MUTATION,
} from "./invoice-email-actions.graphql";
import {
	actionsRowClass,
	confirmEmailClass,
	dialogFooterClass,
	emailFieldClass,
	emailRowClass,
	hintClass,
	notEditableHintClass,
	wrapperClass,
} from "./invoice-email-actions.styles";
import { type InvoiceEmailActionsProps } from "./invoice-email-actions.types";

type PendingAction = "claimEmail" | "invoice";

export const InvoiceEmailActions = ({
	orderFragmentRef,
	onHistoryChanged,
}: InvoiceEmailActionsProps) => {
	const { t } = useTranslation("common");
	const order = useFragment<invoiceEmailActions_OrderFragment$key>(
		ORDER_FRAGMENT,
		orderFragmentRef,
	);

	const currentInvoiceEmail = order.customerDetails?.invoiceEmail ?? "";
	const [invoiceEmail, setInvoiceEmail] = useState(currentInvoiceEmail);
	const [pendingAction, setPendingAction] = useState<PendingAction | undefined>(undefined);

	const [correctInvoiceEmail, isCorrecting] =
		useMutation<invoiceEmailActions_CorrectInvoiceEmailMutation>(
			CORRECT_INVOICE_EMAIL_MUTATION,
		);
	const [sendClaimEmail, isSendingClaimEmail] =
		useMutation<invoiceEmailActions_SendGuestOrderClaimEmailMutation>(
			SEND_CLAIM_EMAIL_MUTATION,
		);
	const [resendInvoice, isResendingInvoice] =
		useMutation<invoiceEmailActions_ResendInvoiceMutation>(RESEND_INVOICE_MUTATION);

	const isPaid = isOrderPaid(order.status);
	const isAwaitingAccount = order.status === "PurchasedAwaitingAccount";
	const hasIssuedInvoice = !!order.invoiceData?.invoiceNumber;
	const isBusy = isCorrecting || isSendingClaimEmail || isResendingInvoice;

	const trimmedEmail = invoiceEmail.trim();
	const hasChanges = trimmedEmail !== currentInvoiceEmail;

	const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);

	const handleSaveOnClick = () => {
		correctInvoiceEmail({
			variables: { input: { orderId: order.id, newInvoiceEmail: trimmedEmail } },
			onCompleted: () => {
				toast.success(t("order.invoiceEmailCorrected", { email: trimmedEmail }));
			},
			onError: () => {
				toast.error(t("order.invoiceEmailCorrectionFailed"));
			},
		});
	};

	const handleConfirmOnClick = () => {
		const action = pendingAction;
		setPendingAction(undefined);

		if (action === "claimEmail") {
			sendClaimEmail({
				variables: { input: { orderId: order.id } },
				onCompleted: (response) => {
					const sentTo =
						response.Admin.Billing.sendGuestOrderClaimEmail?.sentToInvoiceEmail;
					onHistoryChanged();
					toast.success(
						t("order.claimEmailSent", { email: sentTo ?? currentInvoiceEmail }),
					);
				},
				onError: () => {
					toast.error(t("order.claimEmailFailed"));
				},
			});
			return;
		}

		if (action === "invoice") {
			resendInvoice({
				variables: { input: { orderId: order.id } },
				onCompleted: (response) => {
					const sentTo = response.Admin.Billing.resendInvoice?.sentToInvoiceEmail;
					onHistoryChanged();
					toast.success(
						t("order.invoiceResent", { email: sentTo ?? currentInvoiceEmail }),
					);
				},
				onError: () => {
					toast.error(t("order.invoiceResendFailed"));
				},
			});
		}
	};

	if (!order.customerDetails) return <div>{t("order.noData")}</div>;

	return (
		<div className={wrapperClass}>
			{isPaid ? (
				<>
					<div className={emailRowClass}>
						<div className={emailFieldClass}>
							<InputText
								label={t("billing.invoiceEmail")}
								value={invoiceEmail}
								onChange={(value) => setInvoiceEmail(value ?? "")}
							/>
						</div>
						<Button
							type={ButtonType.Button}
							label={t("actions.save")}
							disabled={isBusy || !hasChanges || !looksLikeEmail}
							onClick={handleSaveOnClick}
						/>
						{hasChanges && (
							<Button
								variant={ButtonVariant.Outline}
								type={ButtonType.Button}
								label={t("actions.cancel")}
								disabled={isBusy}
								onClick={() => setInvoiceEmail(currentInvoiceEmail)}
							/>
						)}
					</div>
					<div className={hintClass}>
						{t("order.invoiceEmailCorrectionHint")}
						<KnownGapInfo
							id="invoice-email-correction-known-gap"
							text={t("order.invoiceEmailCorrectionGap")}
						/>
					</div>
				</>
			) : (
				<>
					<div>
						{t("billing.invoiceEmail")}: {currentInvoiceEmail}
					</div>
					<div className={notEditableHintClass}>
						{t("order.invoiceEmailNotCorrectableHint")}
					</div>
				</>
			)}

			<div className={actionsRowClass}>
				<Button
					type={ButtonType.Button}
					label={t("order.sendClaimEmail")}
					disabled={isBusy || !isAwaitingAccount || hasChanges}
					onClick={() => setPendingAction("claimEmail")}
				/>
				<Button
					type={ButtonType.Button}
					label={t("order.resendInvoice")}
					disabled={isBusy || !hasIssuedInvoice || hasChanges}
					onClick={() => setPendingAction("invoice")}
				/>
				<KnownGapInfo id="resend-invoice-known-gap" text={t("order.resendInvoiceGap")} />
			</div>
			{hasChanges && (
				<div className={notEditableHintClass}>{t("order.saveBeforeSending")}</div>
			)}

			<Dialog
				header={
					pendingAction === "claimEmail"
						? t("order.sendClaimEmail")
						: t("order.resendInvoice")
				}
				visible={pendingAction !== undefined}
				onHide={() => setPendingAction(undefined)}
				style={{ width: "32rem" }}
				footer={
					<div className={dialogFooterClass}>
						<Button
							variant={ButtonVariant.Outline}
							type={ButtonType.Button}
							label={t("actions.cancel")}
							onClick={() => setPendingAction(undefined)}
						/>
						<Button
							type={ButtonType.Button}
							label={t("actions.accept")}
							onClick={handleConfirmOnClick}
						/>
					</div>
				}
			>
				<div>
					{pendingAction === "claimEmail"
						? t("order.sendClaimEmailConfirm")
						: t("order.resendInvoiceConfirm")}
				</div>
				<div className={confirmEmailClass}>{currentInvoiceEmail}</div>
			</Dialog>
		</div>
	);
};
